
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Video from 'plaid-threads/Icons/Video';
import Libraries from 'plaid-threads/Icons/Libraries';
import External from 'plaid-threads/Icons/External';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  layout: 'guide',
  description: 'Learn about Bank Income features and implementation',
  section: 'Income',
  title: 'Bank Income'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Header title='Bank Income' subtitle={metadata.description} mdxType="Header" />
    <Actions.SmallWrapper miniTitle='Get started with Bank Income'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/income/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='https://github.com/plaid/income-sample' />
  <Actions.SmallCard icon={<External mdxType="External" />} title='Income Verification Demo' link='https://plaid.coastdemo.com/share/66fb0a180582208ffa82103e?zoom=100' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Bank Income allows you to instantly retrieve net income information from a user-connected bank account, supporting both irregular or gig income and W-2 income. Data available includes a breakdown of income streams to the account, as well as recent and historical income.`}</p>
    <YouTubeEmbed videoId='Hc-MpibSxJE' alignment='full' title='Plaid Income in 3 minutes' caption='Prefer to learn by watching? Get an overview of how Income works in just 3 minutes!' mdxType="YouTubeEmbed" />
    <Callout mdxType="Callout">
      <p>{`For all new integrations supporting end users based in the US, Plaid recommends the use of `}<a parentName="p" {...{
          "href": "/docs/check"
        }}>{`Consumer Report`}</a>{` instead of Bank Income. Consumer Report's income solution provides FCRA compliant insights and bundles income calculations (historical average income, forecasted income, predicted next payment date) in a single report. For more information, see `}<a parentName="p" {...{
          "href": "/docs/check"
        }}>{`Plaid Check Consumer Report`}</a>{`.`}</p>
    </Callout>
    <h4 {...{
      "id": "no-code-income-integration-with-the-credit-dashboard"
    }}>{`No-code Income integration with the Credit Dashboard`}</h4>
    <p>{`Bank Income can be used as part of a no-code integration flow. This integration mode is available only when using Income alongside Assets. For more details, see the `}<a parentName="p" {...{
        "href": "/docs/assets#no-code-integration-with-the-credit-dashboard"
      }}>{`Assets no-code integration guide`}</a>{`.`}</p>
    <h4 {...{
      "id": "integration-process"
    }}>{`Integration process`}</h4>
    <Callout mdxType="Callout">
      <p>{`The flow below describes the latest integration pattern for Plaid Income. If you are an existing customer who integrated before August 17, 2026, your API calls have a slightly different interface. For details on the differences, see `}<a parentName="p" {...{
          "href": "/docs/api/users/user-apis/"
        }}>{`New User APIs`}</a>{`.`}</p>
    </Callout>
    <ol>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/user/create`}</inlineCode>{` to create a `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` that will represent the end user interacting with your application. This step will only need to be done once per end user. If you are using multiple Income types, do not repeat this step when switching to a different Income type.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`. In addition to the required parameters, you will need to provide the following:`}
        <ul parentName="li">
          <li parentName="ul">{`For `}<inlineCode parentName="li">{`user_id`}</inlineCode>{`, provide the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` from `}<inlineCode parentName="li">{`/user/create`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`For `}<inlineCode parentName="li">{`products`}</inlineCode>{`, use `}<inlineCode parentName="li">{`["income_verification"]`}</inlineCode>{`. You can also specify additional products. For more details, see `}<a parentName="li" {...{
              "href": "#using-bank-income-with-other-products"
            }}>{`Using Bank Income with other products`}</a>{`.`}</li>
          <li parentName="ul">{`For `}<inlineCode parentName="li">{`income_verification.income_source_types`}</inlineCode>{`, use `}<inlineCode parentName="li">{`bank`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`Set `}<inlineCode parentName="li">{`income_verification.bank_income.days_requested`}</inlineCode>{` to the desired number of days.`}</li>
          <li parentName="ul">{`Provide a `}<inlineCode parentName="li">{`webhook`}</inlineCode>{` URI with the endpoint where you will receive Plaid webhooks.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`On the client side, create an instance of Link using the `}<inlineCode parentName="li">{`link_token`}</inlineCode>{` returned by `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`; for more details, see the `}<a parentName="li" {...{
          "href": "/docs/link"
        }}>{`Link documentation`}</a>{`.`}</li>
      <li parentName="ol">{`Open Link in your web or mobile client and listen to the `}<inlineCode parentName="li">{`onSuccess`}</inlineCode>{` and `}<inlineCode parentName="li">{`onExit`}</inlineCode>{` callbacks, which will fire once the user has finished or exited the Link session.`}</li>
      <li parentName="ol">{`If you are using other Plaid products such as Auth or Balance alongside Bank Income, call `}<inlineCode parentName="li">{`/link/token/get`}</inlineCode>{` and make sure to capture each `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` from the `}<inlineCode parentName="li">{`link_sessions[].results.item_add_results`}</inlineCode>{` array. Exchange each `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="li">{`access_token`}</inlineCode>{` using `}<inlineCode parentName="li">{`/item/public_token/exchange`}</inlineCode>{`. For more details on token exchange, see the `}<a parentName="li" {...{
          "href": "/docs/api/items/#token-exchange-flow"
        }}>{`Token exchange flow`}</a>{`.`}</li>
      <li parentName="ol">{`To retrieve data, call `}<inlineCode parentName="li">{`/credit/bank_income/get`}</inlineCode>{` with the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{`.`}</li>
    </ol>
    <h4 {...{
      "id": "multi-item-sessions"
    }}>{`Multi-Item sessions`}</h4>
    <p>{`Many users get income deposited into multiple institutions. To help capture a user's full income profile, you can allow your users to link multiple accounts within the same link session on web integrations.`}</p>
    <Image src='/assets/img/docs/income/bank_income_multi_item.png' alt="Select bank, enter credentials, choose income transactions, and review income in Plaid's bank income flow." width='624' caption='Bank Income Multi Item Link flow (some panes excluded)' expandable={true} mdxType="Image" />
    <p>{`To enable this flow, see `}<a parentName="p" {...{
        "href": "/docs/link/multi-item-link"
      }}>{`Multi-Item Link`}</a>{`. The previous Income-specific flow, which used the `}<inlineCode parentName="p">{`income_verification.bank_income.enable_multiple_items`}</inlineCode>{` setting in `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` and obtained public tokens from the `}<inlineCode parentName="p">{`/credit/sessions/get`}</inlineCode>{` endpoint, has been deprecated and replaced with a generic Multi-Item Link flow that supports all Plaid and Plaid Check products.`}</p>
    <h4 {...{
      "id": "using-bank-income-with-other-products"
    }}>{`Using Bank Income with other products`}</h4>
    <p>{`Bank Income Items are fully compatible with other Plaid Item-based products, including Auth, Transactions, Balance, and Assets.`}</p>
    <p>{`When initializing Link, if you plan to use Bank Income and Assets in the same session, it is recommended to put both `}<inlineCode parentName="p">{`income_verification`}</inlineCode>{` and `}<inlineCode parentName="p">{`assets`}</inlineCode>{` in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` `}<inlineCode parentName="p">{`products`}</inlineCode>{` array. If you plan to use Bank Income with Transactions, you should not put `}<inlineCode parentName="p">{`transactions`}</inlineCode>{` in the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array, as this may increase latency. For more details, see `}<a parentName="p" {...{
        "href": "/docs/link/initializing-products"
      }}>{`Choosing how to initialize Link`}</a>{`.`}</p>
    <p>{`To capture an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` for use with other products, call `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` after receiving the `}<inlineCode parentName="p">{`onExit`}</inlineCode>{` or `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback from Link, and capture each `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` from the `}<inlineCode parentName="p">{`link_sessions[].results.item_add_results`}</inlineCode>{` array. You can then exchange these `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`s for `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`s using `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{`. For more details, see the `}<a parentName="p" {...{
        "href": "/docs/api/items/#token-exchange-flow"
      }}>{`Token exchange flow`}</a>{`.`}</p>
    <h4 {...{
      "id": "verifying-bank-income-for-existing-items"
    }}>{`Verifying Bank Income for existing Items`}</h4>
    <p>{`If your user has already connected their depository bank account to your application for a different product, you can add Bank Income to the existing Item via `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`update mode`}</a>{`.`}</p>
    <p>{`To do so, in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request described above, populate the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` field with the access token for the Item. If the user connected their account less than two years ago, they can bypass the Link credentials pane and complete just the Income Verification step. Otherwise, they will be prompted to complete the full Plaid Bank Income Link flow.`}</p>
    <h4 {...{
      "id": "institution-coverage"
    }}>{`Institution coverage`}</h4>
    <p>{`To determine whether an institution supports Bank Income, you can use the Dashboard Institutions page (look for "Bank Income") or the `}<inlineCode parentName="p">{`/institutions/get`}</inlineCode>{` endpoint (look for or filter by the `}<inlineCode parentName="p">{`income_verification`}</inlineCode>{` product). The `}<inlineCode parentName="p">{`income`}</inlineCode>{` product returned by these surfaces represents a legacy product and does not indicate coverage for Bank Income.`}</p>
    <h4 {...{
      "id": "testing-bank-income"
    }}>{`Testing Bank Income`}</h4>
    <p>{`In Sandbox, you can use the user `}<inlineCode parentName="p">{`user_bank_income`}</inlineCode>{` with the password `}<inlineCode parentName="p">{`{}`}</inlineCode>{`. The basic `}<a parentName="p" {...{
        "href": "/docs/sandbox/test-credentials/#sandbox-simple-test-credentials"
      }}>{`Sandbox credentials`}</a>{` (`}<inlineCode parentName="p">{`user_good`}</inlineCode>{`/`}<inlineCode parentName="p">{`pass_good`}</inlineCode>{`) will not return data when used to test Bank Income.`}</p>
    <p>{`Plaid also has additional test users for scenarios such as joint accounts, bonus income, and different levels of creditworthiness: for details, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/test-credentials/#credit-and-income-testing-credentials"
      }}>{`Credit and Income testing credentials`}</a>{`.`}</p>
 <Callout mdxType="Callout">
      <p>{`When using special Sandbox test credentials (such as `}<inlineCode parentName="p">{`user_bank_income`}</inlineCode>{` / `}<inlineCode parentName="p">{`{}`}</inlineCode>{`), use the `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{` flow or a non-OAuth test institution, such as First Platypus Bank (`}<inlineCode parentName="p">{`ins_109508`}</inlineCode>{`). Special test credentials may be ignored when using the Sandbox Link OAuth flow.`}</p>
    </Callout>
    <p>{`If you'd like to test Bank Income with custom data, Plaid provides several `}<a parentName="p" {...{
        "href": "https://github.com/plaid/sandbox-custom-users/tree/main/income"
      }}>{`test JSON configuration objects`}</a>{`. The easiest way to load one is to paste it into a new custom user's configuration object on the `}<strong parentName="p">{`Users`}</strong>{` tab of `}<a parentName="p" {...{
        "href": "/docs/sandbox/studio/"
      }}>{`Sandbox Studio`}</a>{`, rather than passing it as `}<inlineCode parentName="p">{`options.override_password`}</inlineCode>{` yourself.`}</p>
    <p><inlineCode parentName="p">{`/credit/bank_income/get`}</inlineCode>{` can optionally be tested in Sandbox without using Link. Call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` and pass the returned `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` to `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{`. `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{` must be called with the following request body:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #/sandbox/public_token/create request for Bank Income testing",
        "switcher": "false",
        "#/sandbox/public_token/create": true,
        "request": true,
        "for": true,
        "Bank": true,
        "Income": true,
        "testing": true
      }}>{`{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "institution_id": "ins_20", //any valid institution id is fine
  "initial_products": ["income_verification"],
  "user_id": "usr_9nSp2KuZ2x4JDw", //use the user_id from the \`/user/create\` call made earlier
  "options": {
    "override_username": "user_bank_income", //or other test user from Credit and Income testing credentials
    "override_password": "{}",
    "income_verification": {
      "income_source_types": ["bank"],
      "bank_income": {
        "days_requested": 120 //any number of days up to 731 is valid
      }
    }
  }
}
`}</code></pre>
    <p>{`After calling `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{`, call `}<inlineCode parentName="p">{`/credit/bank_income/get`}</inlineCode>{` using the same `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`. The output of `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{` will not be used, but calling it initializes the user for testing.`}</p>
    <h4 {...{
      "id": "bank-income-pricing"
    }}>{`Bank Income pricing`}</h4>
    <p>{`Bank Income is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#one-time-fee"
      }}>{`one-time fee model`}</a>{`. To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`apply for Production access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. For more details about pricing and billing models, see `}<a parentName="p" {...{
        "href": "/docs/account/billing"
      }}>{`Plaid billing`}</a>{`.`}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`If you're ready to launch to Production, see the `}<a parentName="p" {...{
        "href": "/docs/launch-checklist/"
      }}>{`Launch checklist`}</a>{`.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Rocket mdxType="Rocket" />} title='Launch Center' subtitle='See next steps to launch in Production' link='https://dashboard.plaid.com/developers/launch-center' linkText='Launch' />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "no-code-income-integration-with-the-credit-dashboard",
        level: 4,
        title: "No-code Income integration with the Credit Dashboard",
      },
{
        id: "integration-process",
        level: 4,
        title: "Integration process",
      },
{
        id: "multi-item-sessions",
        level: 4,
        title: "Multi-Item sessions",
      },
{
        id: "using-bank-income-with-other-products",
        level: 4,
        title: "Using Bank Income with other products",
      },
{
        id: "verifying-bank-income-for-existing-items",
        level: 4,
        title: "Verifying Bank Income for existing Items",
      },
{
        id: "institution-coverage",
        level: 4,
        title: "Institution coverage",
      },
{
        id: "testing-bank-income",
        level: 4,
        title: "Testing Bank Income",
      },
{
        id: "bank-income-pricing",
        level: 4,
        title: "Bank Income pricing",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
